/**
 * E002-T018: TableSelector Component
 * E012-T018: RBAC Database Visibility Filter
 * E018-S004-T002: External Source Option
 * E018-S008-T005: CrossDoc Badge Indicator
 * S020-8.20.4: Permission-based Filtering (read/write)
 *
 * Dropdown-Komponente zur Auswahl einer Database (Table).
 * Zeigt alle verfügbaren Databases im Workspace, gruppiert nach Doc.
 *
 * RBAC (E012-T018): Verwendet accessibleDatabases$ statt databases$
 * um nur Databases anzuzeigen auf die der User Lesezugriff hat.
 *
 * S020-8.20.4: requiredPermission prop steuert ob Read oder Write Permission
 * geprüft wird. Default ist 'read' für Rückwärtskompatibilität.
 *
 * External Source (E018-S004-T002): Wenn showExternalOption=true, wird
 * "External Source" als erste Option mit Trennstrich angezeigt.
 *
 * CrossDoc Badge (E018-S008-T005): Tables in anderen Docs zeigen ein LinkedPageIcon Badge.
 */

import { Divider, Menu, MenuItem, MenuSub, Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import {
  DatabaseTableViewIcon,
  LinkedPageIcon,
  SearchIcon,
} from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DocService } from '../../../doc';
import {
  filterDocGroupsForHiddenMailFolderDocIds,
  useHiddenMailFolderDocIds,
} from '../../mail/mail-folder-visibility-filter';
import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../services/database-discovery';
import { DropdownChevron } from '../../shared/dropdown-chevron';
import * as styles from '../automation-panel.css';
import { computeSourceSelectorDisplayText } from './table-selector-display-text';

/**
 * E018-S008-T005: CrossDoc Badge Component
 * Shows a link icon when a table is in a different document.
 */
const CrossDocBadge: FC = () => {
  const t = useI18n();
  return (
    <Tooltip content={t['com.streamlain.streamlain.input.cross-doc-table']()}>
      <span
        style={{
          marginLeft: '4px',
          opacity: 0.6,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        <LinkedPageIcon style={{ width: '14px', height: '14px' }} />
      </span>
    </Tooltip>
  );
};

interface DocGroup {
  docId: string;
  docTitle: string;
  databases: DatabaseInfo[];
}

interface SourceTableOption {
  /** Source table info for "Same as Source" option */
  sourceTable: DatabaseInfo;
  /** Label for the option (e.g., "Same as Source (TableName)") */
  label: string;
}

/** An explicit "no table" choice listed above the tables. */
export interface NoneTableOption {
  label: string;
  hint?: string;
  selected: boolean;
  onSelect: () => void;
}

/** Tables this selector does not offer, with the reason shown in their place. */
export interface UnavailableTables {
  matches: (database: DatabaseInfo) => boolean;
  note: string;
}

/**
 * Marks chosen tables by a predicate instead of `value`. With `multiple` the
 * menu stays open and every pick reaches `onChange`, which toggles it.
 */
export interface TableSelection {
  isSelected: (database: DatabaseInfo) => boolean;
  multiple?: boolean;
}

export interface TableSelectorProps {
  /** Currently selected database block ID */
  value: string | null | undefined;
  /** Explicit host when rendered from the canvas portal. */
  currentDocId?: string;
  /** Keep nested menus within a canvas overlay and its focus boundary. */
  menuPortalContainer?: HTMLElement;
  /** Callback when a database is selected */
  onChange: (database: DatabaseInfo) => void;
  /** Placeholder text when no database is selected */
  placeholder?: string;
  /** Block ID to exclude from the list (e.g., source table when showing "Same as Source" option) */
  excludeBlockId?: string | null;
  /** sl-optc: Multiple block IDs to exclude (e.g., already selected tables in multi-output) */
  excludeBlockIds?: string[];
  /** Optional "Same as Source" option to show at the top */
  sourceTableOption?: SourceTableOption;
  /** E018-S004-T002: Show "External Source" option at the top */
  showExternalOption?: boolean;
  /** E018-S004-T002: Callback when "External Source" is selected */
  onExternalSelect?: () => void;
  /**
   * S020-8.20.4: Required permission level for displayed tables.
   * - 'read': Show tables user can read (default, for Input Tab)
   * - 'write': Show tables user can write (for Output Tab)
   */
  requiredPermission?: 'read' | 'write';
  /** Lists an explicit "no table" choice first (e.g. a source-less run). */
  noneOption?: NoneTableOption;
  /** Leaves matching tables out; their document shows the note instead. */
  unavailable?: UnavailableTables;
  selection?: TableSelection;
  disabled?: boolean;
}

export const TableSelector: FC<TableSelectorProps> = ({
  value,
  currentDocId: hostDocId,
  menuPortalContainer,
  onChange,
  placeholder,
  excludeBlockId,
  excludeBlockIds = [],
  sourceTableOption,
  showExternalOption: _showExternalOption,
  onExternalSelect: _onExternalSelect,
  requiredPermission = 'read',
  noneOption,
  unavailable,
  selection,
  disabled = false,
}) => {
  const t = useI18n();
  const portalOptions = menuPortalContainer
    ? { container: menuPortalContainer }
    : undefined;
  const [searchQuery, setSearchQuery] = useState('');
  const [subSearchQueries, setSubSearchQueries] = useState<
    Record<string, string>
  >({});
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const discoveryService = useService(DatabaseDiscoveryService);
  // E018-S008-T005: Get current doc ID for CrossDoc detection
  const docService = useServiceOptional(DocService);
  const currentDocId = hostDocId ?? docService?.doc.id;

  const groupRefs = useRef(
    new Map<
      string,
      ReturnType<DatabaseDiscoveryService['acquireDatabaseGroup']>
    >()
  );
  const [loadingGroups, setLoadingGroups] = useState<ReadonlySet<string>>(
    new Set()
  );
  const releaseGroups = useCallback(() => {
    for (const entry of groupRefs.current.values()) entry.release();
    groupRefs.current.clear();
  }, []);
  useEffect(() => releaseGroups, [releaseGroups]);
  const openGroup = (docId: string, open: boolean) => {
    if (!open || groupRefs.current.has(docId)) return;
    const entry = discoveryService.acquireDatabaseGroup(
      docId,
      requiredPermission
    );
    groupRefs.current.set(docId, entry);
    setLoadingGroups(previous => new Set(previous).add(docId));
    void entry.ready
      .catch(error => {
        if (groupRefs.current.get(docId) === entry) {
          console.warn('[TableSelector] Failed to load table metadata', error);
          setSelectionError(true);
        }
      })
      .finally(() => {
        if (groupRefs.current.get(docId) === entry)
          setLoadingGroups(previous => {
            const next = new Set(previous);
            next.delete(docId);
            return next;
          });
      });
  };

  const [selectionPending, setSelectionPending] = useState(false);
  const [selectionError, setSelectionError] = useState(false);
  // A table listed from the index only reveals its role once resolved.
  const [selectionUnavailable, setSelectionUnavailable] = useState(false);
  const selectionRevision = useRef(0);
  useEffect(
    () => () => {
      selectionRevision.current++;
    },
    []
  );
  const selectDatabase = async (database: DatabaseInfo) => {
    const revision = ++selectionRevision.current;
    setSelectionPending(true);
    setSelectionError(false);
    setSelectionUnavailable(false);
    try {
      const selected = await discoveryService.resolveDatabaseSelection(
        database,
        requiredPermission
      );
      if (revision !== selectionRevision.current) return;
      if (unavailable?.matches(selected)) setSelectionUnavailable(true);
      else onChange(selected);
    } catch {
      if (revision === selectionRevision.current) setSelectionError(true);
    } finally {
      if (revision === selectionRevision.current) setSelectionPending(false);
    }
  };

  // S020-8.20.4: Select the appropriate permission-filtered stream
  // - 'read': accessibleDatabases$ (Doc_Read permission)
  // - 'write': writableDatabases$ (Doc_Update permission)
  const rawDatabasesByDoc = useLiveData(
    requiredPermission === 'write'
      ? discoveryService.writableDatabasesByDoc$
      : discoveryService.accessibleDatabasesByDoc$
  );
  const rawDatabases = useLiveData(
    requiredPermission === 'write'
      ? discoveryService.writableDatabases$
      : discoveryService.accessibleDatabases$
  );
  const hiddenMailFolderDocIds = useHiddenMailFolderDocIds();
  const visibleDatabasesByDoc = useMemo(
    () =>
      filterDocGroupsForHiddenMailFolderDocIds(
        rawDatabasesByDoc,
        hiddenMailFolderDocIds
      ),
    [hiddenMailFolderDocIds, rawDatabasesByDoc]
  );
  const databases = useMemo(
    () => rawDatabases.filter(db => !hiddenMailFolderDocIds.has(db.docId)),
    [hiddenMailFolderDocIds, rawDatabases]
  );

  // sl-optc: Build set of all excluded block IDs
  const excludedIds = useMemo(() => {
    const ids = new Set<string>(excludeBlockIds);
    if (excludeBlockId) ids.add(excludeBlockId);
    return ids;
  }, [excludeBlockId, excludeBlockIds]);

  const docIdsWithUnavailableTables = useMemo(() => {
    const docIds = new Set<string>();
    if (!unavailable) return docIds;
    for (const docGroup of visibleDatabasesByDoc) {
      if (docGroup.databases.some(unavailable.matches)) {
        docIds.add(docGroup.docId);
      }
    }
    return docIds;
  }, [unavailable, visibleDatabasesByDoc]);

  // Filter out excluded and unavailable blocks from the list
  const databasesByDoc = useMemo(() => {
    if (excludedIds.size === 0 && !unavailable) return visibleDatabasesByDoc;

    return visibleDatabasesByDoc
      .map((docGroup: DocGroup) => ({
        ...docGroup,
        databases: docGroup.databases.filter(
          (db: DatabaseInfo) =>
            !excludedIds.has(db.blockId) && !unavailable?.matches(db)
        ),
      }))
      .filter((docGroup: DocGroup) => docGroup.databases.length > 0);
  }, [visibleDatabasesByDoc, excludedIds, unavailable]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredDatabasesByDoc = useMemo(() => {
    if (!normalizedQuery) return databasesByDoc;
    return databasesByDoc
      .map((docGroup: DocGroup) => {
        const docMatches = docGroup.docTitle
          .toLowerCase()
          .includes(normalizedQuery);
        if (docMatches) {
          return docGroup;
        }
        const filteredDatabases = docGroup.databases.filter(
          (db: DatabaseInfo) =>
            db.databaseTitle.toLowerCase().includes(normalizedQuery)
        );
        return {
          ...docGroup,
          databases: filteredDatabases,
        };
      })
      .filter((docGroup: DocGroup) => docGroup.databases.length > 0);
  }, [databasesByDoc, normalizedQuery]);

  const currentDocGroup = currentDocId
    ? filteredDatabasesByDoc.find(group => group.docId === currentDocId)
    : undefined;
  const otherDocGroups = currentDocId
    ? filteredDatabasesByDoc.filter(group => group.docId !== currentDocId)
    : filteredDatabasesByDoc;

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        !(activeElement instanceof HTMLElement) ||
        activeElement.dataset.streamlainSearch !== 'true'
      ) {
        setIsSearchFocused(false);
      }
    });
  };

  const pointerFocusGuardProps = isSearchFocused
    ? {
        onPointerMove: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
        onPointerLeave: (event: { preventDefault: () => void }) =>
          event.preventDefault(),
      }
    : undefined;

  const isSelected =
    selection?.isSelected ?? ((db: DatabaseInfo) => db.blockId === value);
  const selectedDatabases = databases.filter(isSelected);
  // Find currently selected database info
  const selectedDatabase = selectedDatabases[0] ?? null;

  // Check if "Same as Source" is selected
  const isSourceSelected =
    sourceTableOption && value === sourceTableOption.sourceTable.blockId;

  // E018-S008-T005: Check if selected database is CrossDoc
  const isSelectedCrossDoc =
    selectedDatabase && currentDocId && selectedDatabase.docId !== currentDocId;

  const displayText = noneOption?.selected
    ? noneOption.label
    : computeSourceSelectorDisplayText({
        sourceTableLabel: isSourceSelected
          ? (sourceTableOption?.label ?? null)
          : null,
        selectedDatabase:
          selection?.multiple && selectedDatabases.length > 1
            ? {
                ...selectedDatabases[0],
                databaseTitle: selectedDatabases
                  .map(db => db.databaseTitle)
                  .join(', '),
              }
            : selectedDatabase,
        isCrossDoc: Boolean(isSelectedCrossDoc),
        placeholder:
          placeholder ?? t['com.streamlain.streamlain.input.select-table'](),
      });

  const renderSearchItem = (
    query: string,
    onQueryChange: (v: string) => void
  ) => (
    <MenuItem
      onSelect={event => event.preventDefault()}
      className={styles.menuSearchItem}
      {...pointerFocusGuardProps}
    >
      <div
        className={styles.menuSearchContent}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => event.stopPropagation()}
      >
        <SearchIcon className={styles.menuSearchIcon} />
        <input
          type="text"
          value={query}
          onChange={event => onQueryChange(event.target.value)}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          placeholder={t['com.streamlain.streamlain.input.search-sources']()}
          className={styles.menuSearchInput}
          data-streamlain-search="true"
        />
      </div>
    </MenuItem>
  );

  const renderDocGroupItems = (docGroup: DocGroup) => {
    const rawQuery = subSearchQueries[docGroup.docId] ?? '';
    const groupQuery = rawQuery.trim().toLowerCase();
    const filteredDatabases = groupQuery
      ? docGroup.databases.filter((db: DatabaseInfo) =>
          db.databaseTitle.toLowerCase().includes(groupQuery)
        )
      : docGroup.databases;

    const handleSubSearchChange = (value: string) => {
      setSubSearchQueries(prev => ({
        ...prev,
        [docGroup.docId]: value,
      }));
    };

    return (
      <>
        {renderSearchItem(rawQuery, handleSubSearchChange)}
        <Divider />
        {loadingGroups.has(docGroup.docId) ? (
          <MenuItem disabled>
            {t['com.streamlain.database.picker.loading']()}
          </MenuItem>
        ) : filteredDatabases.length === 0 ? (
          <MenuItem disabled {...pointerFocusGuardProps}>
            {t['com.streamlain.streamlain.input.no-tables-found']()}
          </MenuItem>
        ) : (
          filteredDatabases.map((db: DatabaseInfo) => (
            <MenuItem
              key={db.blockId}
              prefixIcon={<DatabaseTableViewIcon />}
              selected={isSelected(db)}
              disabled={selectionPending}
              onSelect={
                selection?.multiple
                  ? event => event.preventDefault()
                  : undefined
              }
              onClick={() => void selectDatabase(db)}
              {...pointerFocusGuardProps}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {db.databaseTitle}
                {currentDocId && docGroup.docId !== currentDocId && (
                  <CrossDocBadge />
                )}
              </span>
            </MenuItem>
          ))
        )}
        {unavailable && docIdsWithUnavailableTables.has(docGroup.docId) && (
          <>
            <Divider />
            <div role="note" className={styles.menuNote}>
              {unavailable.note}
            </div>
          </>
        )}
      </>
    );
  };

  const menuItems = (
    <>
      {renderSearchItem(searchQuery, setSearchQuery)}
      <Divider />

      {noneOption && (
        <>
          <MenuItem
            selected={noneOption.selected}
            onClick={noneOption.onSelect}
            {...pointerFocusGuardProps}
          >
            <span className={styles.noneOption}>
              <span>{noneOption.label}</span>
              {noneOption.hint && (
                <span className={styles.noneOptionHint}>{noneOption.hint}</span>
              )}
            </span>
          </MenuItem>
          <Divider />
        </>
      )}

      {/* "Same as Source" option */}
      {sourceTableOption && (
        <>
          <MenuItem
            prefixIcon={<DatabaseTableViewIcon />}
            selected={isSourceSelected}
            disabled={selectionPending}
            onClick={() => void selectDatabase(sourceTableOption.sourceTable)}
            {...pointerFocusGuardProps}
          >
            {sourceTableOption.label}
          </MenuItem>
          <Divider />
        </>
      )}

      {/* Other tables */}
      {filteredDatabasesByDoc.length === 0 ? (
        <MenuItem disabled {...pointerFocusGuardProps}>
          {t['com.streamlain.streamlain.input.no-tables-found']()}
        </MenuItem>
      ) : (
        <>
          {currentDocGroup && (
            <>
              <MenuSub
                portalOptions={portalOptions}
                key={currentDocGroup.docId}
                subOptions={{
                  onOpenChange: open => openGroup(currentDocGroup.docId, open),
                }}
                items={renderDocGroupItems(currentDocGroup)}
                triggerOptions={pointerFocusGuardProps}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <LinkedPageIcon
                    style={{
                      width: '16px',
                      height: '16px',
                      marginRight: '8px',
                    }}
                  />
                  {currentDocGroup.docTitle}
                </span>
              </MenuSub>
              {otherDocGroups.length > 0 && <Divider />}
            </>
          )}
          {otherDocGroups.map((docGroup: DocGroup) => {
            const isCrossDocGroup =
              currentDocId && docGroup.docId !== currentDocId;

            return (
              <MenuSub
                portalOptions={portalOptions}
                key={docGroup.docId}
                subOptions={{
                  onOpenChange: open => openGroup(docGroup.docId, open),
                }}
                items={renderDocGroupItems(docGroup)}
                triggerOptions={pointerFocusGuardProps}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <LinkedPageIcon
                    style={{
                      width: '16px',
                      height: '16px',
                      marginRight: '8px',
                    }}
                  />
                  {docGroup.docTitle}
                  {isCrossDocGroup && <CrossDocBadge />}
                </span>
              </MenuSub>
            );
          })}
        </>
      )}
    </>
  );

  return (
    <>
      <Menu
        items={menuItems}
        portalOptions={portalOptions}
        rootOptions={{ onClose: releaseGroups }}
      >
        <button
          className={styles.fieldRow}
          type="button"
          disabled={selectionPending || disabled}
          aria-busy={selectionPending}
          style={{ cursor: 'pointer', width: '100%' }}
        >
          <DatabaseTableViewIcon style={{ flexShrink: 0, opacity: 0.6 }} />
          <span
            className={styles.fieldLabel}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {selectionPending
              ? t['com.streamlain.database.picker.loading']()
              : displayText}
            {isSelectedCrossDoc && <CrossDocBadge />}
          </span>
          <DropdownChevron />
        </button>
      </Menu>
      {selectionError && (
        <div
          role="alert"
          style={{
            color: cssVarV2('status/error'),
            fontSize: 12,
            marginTop: 4,
          }}
        >
          {t['com.streamlain.database.picker.load-failed']()}
        </div>
      )}
      {unavailable &&
        (selectionUnavailable ||
          (selectedDatabase && unavailable.matches(selectedDatabase))) && (
          <div
            role="alert"
            style={{
              color: cssVarV2('status/error'),
              fontSize: 12,
              marginTop: 4,
            }}
          >
            {unavailable.note}
          </div>
        )}
    </>
  );
};
